import {
  GetSharedTodayUpdateResponse,
  SharableTodayUpdate,
} from '@dreamai/types';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api';
import { formatPostedLabelForDay } from '../../utils/formatPostedLabelForDay';
import ErrorState from '../common/ErrorState';
import './shared-today-update-page.scss';
import './today-page.scss';

const SharedTodayUpdatePage: React.FC = () => {
  const { updateId, imageId } = useParams<{
    updateId: string;
    imageId: string;
  }>();
  const [update, setUpdate] = useState<SharableTodayUpdate | null>(null);

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    const fetchSharedUpdate = async () => {
      if (!updateId) {
        setFetchError(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get<GetSharedTodayUpdateResponse>(
          `/today/updates/shared/${updateId}/${imageId}`,
        );

        const payload = response.data as GetSharedTodayUpdateResponse;
        const sharedUpdate = payload.update;

        if (!sharedUpdate || !sharedUpdate.updateId) {
          setUpdate(null);
          setFetchError(true);
          return;
        }

        setUpdate(sharedUpdate);
        setFetchError(false);
      } catch (error) {
        console.error('Error fetching shared update:', error);
        setUpdate(null);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSharedUpdate();
  }, [updateId]);

  const agentName = update?.agentName || 'Assistant';

  return (
    <div className='today-page inset-white-backed shared-update-page'>
      <div className='today-content'>
        {loading && (
          <div className='shared-update-single'>
            <div className='images-section'>
              <div className='images-scroll'>
                <div className='image-card ghost-card'>
                  <div className='ghost-image' />
                  <div className='image-caption-row'>
                    <div className='ghost-avatar' />
                    <div className='ghost-texts'>
                      <div className='ghost-line' style={{ width: 91 }} />
                      <div className='ghost-line' style={{ width: 64 }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && fetchError && (
          <div className='update-section'>
            <div className='update-body'>
              <ErrorState
                title='Unable to load shared update'
                subtitle='This shared update may be unavailable or expired.'
              />
            </div>
          </div>
        )}

        {!loading && !fetchError && update && !update.image && (
          <div className='update-section'>
            <div className='update-body'>
              <div className='empty-state'>
                <p>No image found for this shared update.</p>
              </div>
            </div>
          </div>
        )}

        {!loading && !fetchError && update?.image && (
          <div className='shared-update-single'>
            <div className='images-section'>
              <div className='images-scroll'>
                <div className='image-card'>
                  {update.image.imageUrl && (
                    <img
                      src={update.image.imageUrl}
                      alt={update.image.caption || 'Shared update image'}
                      className='update-image'
                    />
                  )}
                  {update.image.caption && (
                    <div className='image-caption-row'>
                      {update.agentImageUrl && (
                        <div className='dreamie-image-wrapper'>
                          <img src={update.agentImageUrl} alt={agentName} />
                        </div>
                      )}
                      <div className='caption-content'>
                        <p className='caption-text'>
                          <b>{agentName}</b> {update.image.caption}
                        </p>
                        <span className='caption-time'>
                          {formatPostedLabelForDay(update.image.createdAt)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='shared-update-created-by'>
              <span className='shared-update-created-by-label'>Created by</span>
              <Link
                to='https://usedream.ai/'
                className='shared-update-logo-link'
                aria-label='Dream home'
              >
                <img
                  src='/dream-logo-mobile.svg'
                  alt='Dream'
                  className='shared-update-logo'
                />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedTodayUpdatePage;
